import {
  ChangeDetectionStrategy,
  Component,
  DestroyRef,
  EventEmitter,
  inject,
  Output,
  signal,
  ViewChild,
} from '@angular/core';
import {CommonModule} from '@angular/common';
import {FormsModule} from '@angular/forms';
import {takeUntilDestroyed} from '@angular/core/rxjs-interop';
import {Subject} from 'rxjs';
import {debounceTime, distinctUntilChanged, switchMap} from 'rxjs/operators';
import {FontAwesomeModule} from '@fortawesome/angular-fontawesome';
import {faWandMagicSparkles} from '@fortawesome/free-solid-svg-icons';
import {UiInputComponent, UiModalComponent} from '../../../shared/ui';
import {MealPlanStore} from '../../../services/meal-plan/meal-plan-store.service';
import {DialogService} from '../../../services/dialog/dialog.service';
import {MealPlanEntry, RecipeSearchResult, Slot} from '../../../services/meal-plan/meal-plan.models';
import {keyToLocalDate} from '../../../utils/date-key.util';

interface PickerTarget {
  dateKey: string;
  slot: Slot;
}

/**
 * Add-to-slot picker: debounced recipe search over the existing recipe API,
 * plus a free-text escape hatch. Opened for a specific `(date, slot)` cell
 * (`open`) or for the pool (`openForPool`).
 *
 * Also offers an "ask the assistant" entry that just opens the global dialog
 * chat (see `openAssistant`), so the user can generate a recipe in conversation.
 */
@Component({
  selector: 'app-slot-picker',
  standalone: true,
  imports: [CommonModule, FormsModule, FontAwesomeModule, UiModalComponent, UiInputComponent],
  templateUrl: './slot-picker.component.html',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class SlotPickerComponent {
  private readonly store = inject(MealPlanStore);
  private readonly dialog = inject(DialogService);
  private readonly destroyRef = inject(DestroyRef);

  protected readonly faWandMagicSparkles = faWandMagicSparkles;

  @ViewChild(UiModalComponent) private modal!: UiModalComponent;

  @Output() recipeChosen = new EventEmitter<{dateKey: string; slot: Slot; recipe: RecipeSearchResult}>();
  @Output() freeTextChosen = new EventEmitter<{dateKey: string; slot: Slot; title: string}>();
  /** A pool candidate was picked to fill the target cell (cell mode only). */
  @Output() poolItemChosen = new EventEmitter<{dateKey: string; slot: Slot; entry: MealPlanEntry}>();
  /** Pool mode: chosen candidate goes to the pool (unplaced), not a cell. */
  @Output() recipePooled = new EventEmitter<RecipeSearchResult>();
  @Output() freeTextPooled = new EventEmitter<string>();

  /**
   * The household pool (unplaced candidates). Offered as quick-pick when adding
   * to a cell so the user can schedule an existing idea without re-searching;
   * hidden in pool mode (adding to the pool from the pool makes no sense).
   */
  protected readonly pool = this.store.pool;

  protected readonly target = signal<PickerTarget | null>(null);
  /** True when opened for the pool (no target cell) rather than a specific slot. */
  protected readonly poolMode = signal(false);
  protected readonly results = signal<RecipeSearchResult[]>([]);
  protected readonly searching = signal(false);
  protected readonly query = signal('');
  protected freeText = '';

  private readonly query$ = new Subject<string>();

  constructor() {
    this.query$
      .pipe(
        debounceTime(250),
        distinctUntilChanged(),
        switchMap((q) => {
          this.searching.set(true);
          return this.store.searchRecipes(q);
        }),
        takeUntilDestroyed(this.destroyRef),
      )
      .subscribe({
        next: (hits) => {
          this.results.set(hits);
          this.searching.set(false);
        },
        error: () => this.searching.set(false),
      });
  }

  get slotLabel(): string {
    const s = this.target()?.slot;
    return s ? this.store.slotLabel(s) : '';
  }

  get dateLabel(): string {
    const key = this.target()?.dateKey;
    return key
      ? new Intl.DateTimeFormat('de-CH', {weekday: 'long', day: 'numeric', month: 'long'}).format(
          keyToLocalDate(key),
        )
      : '';
  }

  open(dateKey: string, slot: Slot): void {
    this.poolMode.set(false);
    this.target.set({dateKey, slot});
    this.resetAndOpen();
  }

  /** Open the picker to add a candidate to the pool (no target cell). */
  openForPool(): void {
    this.poolMode.set(true);
    this.target.set(null);
    this.resetAndOpen();
  }

  private resetAndOpen(): void {
    this.query.set('');
    this.freeText = '';
    this.results.set([]);
    this.query$.next(''); // prime with the full list
    this.modal.open();
  }

  /** Hand off to the global dialog assistant, primed to author a recipe — the
   *  prefilled ask is what makes it call `propose_recipe` and render a preview. */
  openAssistant(): void {
    this.modal.close();
    // Planning a meal: prefer building the recipe around what's already in stock
    // (especially soon-to-expire items), the meal planner's whole point.
    this.dialog.openWith('Erstelle ein neues Rezept für ', {propose_recipe: {pantry: 'prefer'}});
  }

  onQueryChange(q: string): void {
    this.query.set(q);
    this.query$.next(q);
  }

  /** Place an existing pool candidate into the target cell. Cell mode only. */
  choosePoolItem(entry: MealPlanEntry): void {
    const t = this.target();
    if (!t) return;
    this.poolItemChosen.emit({dateKey: t.dateKey, slot: t.slot, entry});
    this.modal.close();
  }

  chooseRecipe(recipe: RecipeSearchResult): void {
    if (this.poolMode()) {
      this.recipePooled.emit(recipe);
      this.modal.close();
      return;
    }
    const t = this.target();
    if (!t) return;
    this.recipeChosen.emit({dateKey: t.dateKey, slot: t.slot, recipe});
    this.modal.close();
  }

  chooseFreeText(): void {
    const title = this.freeText.trim();
    if (!title) return;
    if (this.poolMode()) {
      this.freeTextPooled.emit(title);
      this.modal.close();
      return;
    }
    const t = this.target();
    if (!t) return;
    this.freeTextChosen.emit({dateKey: t.dateKey, slot: t.slot, title});
    this.modal.close();
  }
}
